/**
 * Single home for the "save … and then what?" question every editable PZ/WZ
 * save asks (owner decision 2026-08-11).
 *
 * WHY THIS EXISTS
 * ---------------
 * Saving a warehouse document used to be a half-finished action: the operator
 * pressed "Zapisz", landed on a DRAFT, and then had to reopen the document and
 * click a second, differently-named button ("Przyjmij towar wirtualnie" /
 * "Rozlicz wydanie") to actually move the stock. In practice the stock move IS
 * the intent in most saves, so the second trip was pure friction — and a
 * forgotten second click silently left goods off the stock.
 *
 * So the decision now happens once, at save time, in one dialog with three
 * explicit exits:
 *   - POST   — save AND move the stock (PZ receipt / WZ release)
 *   - DRAFT  — save only, decide later (the old behavior, still one click)
 *   - null   — cancel, nothing is saved
 *
 * The dialog NEVER decides on the operator's behalf and never remembers a
 * previous choice: a document that moves stock is irreversible (PZ RECEIVED /
 * WZ RELEASED can no longer be edited), so it must always be a deliberate,
 * per-save answer.
 *
 * The per-document copy lives in the document's own namespace
 * (`pz:saveIntent.*` / `wz:saveIntent.*`), so callers must have that namespace
 * plus `common` loaded. The stock-moving side effects themselves stay in
 * `pzReceiveFlow.ts` / `wzReleaseFlow.tsx` — this module only asks the
 * question.
 */

import React from 'react';
import { Alert, Button, Space } from 'antd';
import type { ReactNode } from 'react';

export type WarehouseSaveIntent = 'DRAFT' | 'POST';

export type WarehouseSaveIntentDocumentType = 'PZ' | 'WZ';

type TranslateFn = (key: string, options?: Record<string, unknown>) => string;

interface ModalInstanceLike {
  destroy: () => void;
}

interface ModalLike {
  confirm: (config: Record<string, unknown>) => ModalInstanceLike;
}

export interface AskWarehouseSaveIntentParams {
  documentType: WarehouseSaveIntentDocumentType;
  /** `modal` from antd's `App.useApp()` — never the static `Modal.confirm`. */
  modal: ModalLike;
  t: TranslateFn;
  /**
   * Polish sentence explaining why this document cannot move stock yet (e.g. a
   * PZ line without a purchase price). When set, the POST button is disabled
   * and the reason is shown — the operator can still save a draft instead of
   * being bounced with an error after the save already happened.
   */
  postBlockedReason?: string | null;
}

const NAMESPACE_BY_DOCUMENT_TYPE: Record<WarehouseSaveIntentDocumentType, string> = {
  PZ: 'pz',
  WZ: 'wz',
};

/**
 * Resolves to the operator's choice, or `null` when they backed out (cancel
 * button, close icon, Esc). The caller performs the save and — for `POST` —
 * the stock movement; this helper has no side effects of its own.
 */
export function askWarehouseSaveIntent(
  params: AskWarehouseSaveIntentParams,
): Promise<WarehouseSaveIntent | null> {
  const { documentType, modal, t, postBlockedReason } = params;
  const ns = NAMESPACE_BY_DOCUMENT_TYPE[documentType];

  return new Promise((resolve) => {
    let instance: ModalInstanceLike | null = null;
    // `instance` is still null while this closure is created; it is only read
    // on click, after modal.confirm() has returned.
    const finish = (intent: WarehouseSaveIntent | null) => {
      resolve(intent);
      instance?.destroy();
    };

    const content: ReactNode = (
      <div className="warehouse-save-intent">
        <p>{t(`${ns}:saveIntent.description`)}</p>
        {postBlockedReason ? (
          <Alert type="warning" showIcon message={postBlockedReason} />
        ) : null}
      </div>
    );

    instance = modal.confirm({
      title: t(`${ns}:saveIntent.title`),
      content,
      width: 560,
      closable: true,
      maskClosable: false,
      // Esc / close icon = "I did not mean to save at all".
      onCancel: () => resolve(null),
      footer: (
        <Space wrap style={{ justifyContent: 'flex-end', width: '100%' }}>
          <Button onClick={() => finish(null)}>{t('common:cancel')}</Button>
          <Button onClick={() => finish('DRAFT')}>{t(`${ns}:saveIntent.draft`)}</Button>
          <Button
            type="primary"
            disabled={Boolean(postBlockedReason)}
            onClick={() => finish('POST')}
          >
            {t(`${ns}:saveIntent.post`)}
          </Button>
        </Space>
      ),
    });
  });
}
